import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldCheck, WifiOff, MapPin, Check, Ship, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useEventsStore, type FeedEvent } from '@/stores/events';
import { useFleetStore } from '@/stores/fleet';
import { describeEvent } from '@/lib/traccarLabels';
import { eventRisk, RISK_META } from '@/lib/eventRisk';
import { computeMachineStatus, isBlocked, readTelemetry } from '@/lib/machineStatus';
import { cn, formatRelativeTime, formatDateTime } from '@/lib/utils';

const RISK_WEIGHT = { critico: 0, atencao: 1, normal: 2 } as const;
/** SLA de atendimento (minutos) por severidade — estoura se aberto além disso. */
const SLA_MIN = { critico: 15, atencao: 60, normal: 240 } as const;

/** Minutos em aberto desde o evento; null se resolvido. */
function openMinutes(e: FeedEvent, now: number): number {
  return Math.max(0, Math.floor((now - new Date(e.serverTime).getTime()) / 60000));
}
function slaBreached(e: FeedEvent, now: number): boolean {
  if (e.read) return false;
  return openMinutes(e, now) > SLA_MIN[eventRisk(e.type)];
}

export function CentralPage() {
  const items = useEventsStore((s) => s.items);
  const markRead = useEventsStore((s) => s.markRead);
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);

  // tick p/ atualizar tempo em aberto / SLA
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Fila priorizada: crítico/atenção, não-lidos primeiro, mais recentes no topo.
  const queue = useMemo(() => {
    return items
      .filter((e) => eventRisk(e.type) !== 'normal')
      .sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        const rw = RISK_WEIGHT[eventRisk(a.type)] - RISK_WEIGHT[eventRisk(b.type)];
        if (rw !== 0) return rw;
        return new Date(b.serverTime).getTime() - new Date(a.serverTime).getTime();
      })
      .slice(0, 80);
  }, [items]);

  const stats = useMemo(() => {
    let criticos = 0, atencao = 0;
    for (const e of items) {
      if (e.read) continue;
      const r = eventRisk(e.type);
      if (r === 'critico') criticos++;
      else if (r === 'atencao') atencao++;
    }
    let offline = 0, bloqueadas = 0, bateriaBaixa = 0;
    for (const d of devices.values()) {
      const pos = positions.get(d.id);
      if (computeMachineStatus(d, pos) === 'off') offline++;
      if (isBlocked(d, pos)) bloqueadas++;
      const lvl = readTelemetry(pos).batteryLevel;
      if (lvl != null && lvl <= 20) bateriaBaixa++;
    }
    return { criticos, atencao, offline, bloqueadas, bateriaBaixa };
  }, [items, devices, positions]);

  const slaEstourado = useMemo(
    () => items.filter((e) => eventRisk(e.type) !== 'normal' && slaBreached(e, now)).length,
    [items, now],
  );

  const alertVessels = useMemo(() => {
    const out: { id: number; name: string; reason: string }[] = [];
    for (const d of devices.values()) {
      const pos = positions.get(d.id);
      const reasons: string[] = [];
      if (computeMachineStatus(d, pos) === 'off') reasons.push('Offline');
      if (isBlocked(d, pos)) reasons.push('Bloqueada');
      const lvl = readTelemetry(pos).batteryLevel;
      if (lvl != null && lvl <= 20) reasons.push('Bateria baixa');
      if (reasons.length) out.push({ id: d.id, name: d.name, reason: reasons.join(' · ') });
    }
    return out;
  }, [devices, positions]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold">Central 24h</h1>
        <p className="text-sm text-muted-foreground">Fila de alertas críticos e operacionais — monitoramento e atendimento.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Kpi label="Críticos" value={stats.criticos} cls="text-status-blocked" />
        <Kpi label="Atenção" value={stats.atencao} cls="text-status-ready" />
        <Kpi label="SLA estourado" value={slaEstourado} cls="text-status-blocked" />
        <Kpi label="Offline" value={stats.offline} cls="text-status-off" />
        <Kpi label="Bloqueadas" value={stats.bloqueadas} cls="text-status-blocked" />
        <Kpi label="Bateria baixa" value={stats.bateriaBaixa} cls="text-status-ready" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* Fila de atendimento */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border p-3">
              <h2 className="text-sm font-bold">Fila de atendimento</h2>
              {queue.some((e) => !e.read) && (
                <button type="button" onClick={() => markRead(queue.filter((e) => !e.read).map((e) => e.uid))}
                  className="rounded-md border border-border px-2 py-1 text-xs font-semibold hover:bg-accent">Resolver todos</button>
              )}
            </div>
            {queue.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-10 text-center">
                <ShieldCheck className="h-10 w-10 text-status-running" />
                <p className="text-sm text-muted-foreground">Nenhum alerta crítico ou de atenção pendente.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {queue.map((e) => <QueueRow key={e.uid} event={e} now={now} onResolve={() => markRead([e.uid])} />)}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Embarcações em alerta */}
        <Card>
          <CardContent className="p-0">
            <div className="border-b border-border p-3"><h2 className="text-sm font-bold">Embarcações em alerta</h2></div>
            {alertVessels.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">Nenhuma embarcação em estado de alerta.</p>
            ) : (
              <ul className="divide-y divide-border">
                {alertVessels.map((v) => (
                  <li key={v.id}>
                    <Link to={`/devices/${v.id}`} className="flex items-center gap-3 p-3 hover:bg-accent/50">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-status-blocked/10 text-status-blocked"><Ship className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{v.name}</p>
                        <p className="text-xs text-status-blocked">{v.reason}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QueueRow({ event, now, onResolve }: { event: FeedEvent; now: number; onResolve: () => void }) {
  const devices = useFleetStore((s) => s.devices);
  const device = event.deviceId != null ? devices.get(event.deviceId) : undefined;
  const risk = eventRisk(event.type);
  const meta = RISK_META[risk];
  const Icon = risk === 'critico' ? AlertTriangle : risk === 'atencao' ? AlertTriangle : WifiOff;
  const mins = openMinutes(event, now);
  const breached = slaBreached(event, now);
  const slaTxt = mins >= 60 ? `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}` : `${mins}min`;

  return (
    <li className={cn('flex items-start gap-3 p-3', !event.read && 'bg-primary/5')}>
      <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', meta.badge)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', meta.badge)}>{meta.label}</span>
          <p className="truncate text-sm font-semibold">{describeEvent(event)}</p>
          {!event.read && (
            <span className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold',
              breached ? 'bg-status-blocked/15 text-status-blocked' : 'bg-secondary text-muted-foreground',
            )}>
              <Clock className="h-2.5 w-2.5" /> {breached ? `SLA estourado · ${slaTxt}` : `aberto ${slaTxt}`}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {device ? device.name : event.deviceId ? `Embarcação ${event.deviceId}` : 'Sistema'} · {formatRelativeTime(event.serverTime)}
        </p>
        <p className="text-[10px] text-muted-foreground/70">{formatDateTime(event.serverTime)}</p>
      </div>
      <div className="flex shrink-0 flex-col gap-1">
        {device && (
          <Link to={`/map?device=${device.id}`} className="rounded-md p-1.5 text-primary hover:bg-primary/10" title="Ver no mapa"><MapPin className="h-4 w-4" /></Link>
        )}
        {!event.read && (
          <button type="button" onClick={onResolve} className="rounded-md p-1.5 text-status-running hover:bg-status-running/10" title="Resolver"><Check className="h-4 w-4" /></button>
        )}
      </div>
    </li>
  );
}

function Kpi({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3 text-center">
      <p className={cn('text-2xl font-bold leading-none', cls)}>{value}</p>
      <p className="mt-1 text-[11px] font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
